import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
  TableCell,
  TableRow,
  toast
} from "@veritrellis/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { WorkspaceRole } from "@veritrellis/shared/roles";
import { CopyButton } from "../CopyButton.js";
import { apiFetch } from "../../lib/api.js";
import { formatDateTimeShort } from "../../lib/requestPresentation.js";
import { workspaceRoleBadgeVariant } from "../../lib/workspaceRolePresentation.js";
import { userInitialsFromEmail } from "../../lib/userDisplay.js";

export type MemberRow = {
  user_id: string;
  role: WorkspaceRole;
  email: string | null;
  name: string | null;
  joined_at: string;
};

const isWorkspaceRole = (v: string): v is WorkspaceRole =>
  v === "owner" ||
  v === "admin" ||
  v === "developer" ||
  v === "approver" ||
  v === "viewer" ||
  v === "auditor";

type MemberTableRowProps = {
  member: MemberRow;
  workspaceId: string;
  canManage: boolean;
  myUserId: string | null;
  myRole: WorkspaceRole | null;
  assignableRoles: WorkspaceRole[];
};

export const MemberTableRow = ({
  member,
  workspaceId,
  canManage,
  myUserId,
  myRole,
  assignableRoles
}: MemberTableRowProps) => {
  const queryClient = useQueryClient();
  const displayName = member.name ?? member.email ?? "Unknown user";
  const initialsSource = member.email ?? member.user_id;
  const isSelf = myUserId !== null && member.user_id === myUserId;
  const [draftRole, setDraftRole] = useState<WorkspaceRole>(member.role);

  useEffect(() => {
    setDraftRole(member.role);
  }, [member.role]);

  const dirty = draftRole !== member.role;
  const isOwnerRowNonOwnerActor = member.role === "owner" && myRole !== "owner";
  const canEditThisRow =
    canManage && !isSelf && assignableRoles.length > 0 && !isOwnerRowNonOwnerActor;

  const updateRole = useMutation({
    mutationFn: async (nextRole: WorkspaceRole) => {
      if (!assignableRoles.includes(nextRole)) {
        throw new Error("invalid_role");
      }
      const res = await apiFetch(`/admin/workspaces/${workspaceId}/members/${member.user_id}`, {
        method: "PATCH",
        body: JSON.stringify({ role: nextRole })
      });
      if (!res.ok) {
        throw new Error(`patch_${String(res.status)}`);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["members", workspaceId] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "me"] });
      toast.show({
        title: "Role updated",
        message: "Change is effective immediately and recorded in the audit log.",
        color: "green"
      });
    },
    onError: (err: Error) => {
      const msg =
        err.message === "invalid_role" ? "You cannot assign that role." : "Could not update role.";
      toast.show({ title: "Error", message: msg, color: "red" });
    }
  });

  return (
    <TableRow className="admin-members-row">
      <TableCell>
        <div className="admin-members-identity">
          <Avatar className="admin-members-avatar">
            <AvatarFallback className="text-xs font-medium">
              {userInitialsFromEmail(initialsSource)}
            </AvatarFallback>
          </Avatar>
          <div className="admin-members-identity-text">
            <div className="admin-members-name-row">
              <span className="admin-members-name">{displayName}</span>
              {isSelf ? (
                <Badge variant="secondary" className="text-xs">
                  You
                </Badge>
              ) : null}
            </div>
            {member.email !== null ? (
              <p className="admin-members-email">{member.email}</p>
            ) : (
              <p className="admin-members-email admin-members-email--muted">No email on file</p>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div className="admin-members-id-cell">
          <code className="admin-mono-inline">{member.user_id}</code>
          <CopyButton value={member.user_id} label="Copy user ID" />
        </div>
      </TableCell>
      <TableCell>
        <time className="admin-members-joined" dateTime={member.joined_at}>
          {formatDateTimeShort(member.joined_at)}
        </time>
      </TableCell>
      <TableCell>
        {canEditThisRow ? (
          <div className="admin-members-role-editor">
            <Select
              value={assignableRoles.includes(draftRole) ? draftRole : member.role}
              onValueChange={(v) => {
                if (isWorkspaceRole(v)) {
                  setDraftRole(v);
                }
              }}
            >
              <SelectTrigger className="admin-members-role-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {assignableRoles.map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              variant={dirty ? "default" : "secondary"}
              disabled={!dirty || updateRole.isPending}
              onClick={() => updateRole.mutate(draftRole)}
            >
              {updateRole.isPending ? <Spinner className="mr-1 h-3.5 w-3.5" /> : null}
              Save
            </Button>
          </div>
        ) : (
          <div className="admin-members-role-readonly">
            <Badge variant={workspaceRoleBadgeVariant(member.role)}>{member.role}</Badge>
            {isSelf && canManage ? (
              <p className="admin-members-role-note">
                You cannot change your own role here. Ask another owner or admin.
              </p>
            ) : null}
            {!isSelf && isOwnerRowNonOwnerActor && canManage ? (
              <p className="admin-members-role-note">
                Only a workspace owner can change another owner&apos;s role.
              </p>
            ) : null}
          </div>
        )}
      </TableCell>
    </TableRow>
  );
};
